import { useAppTheme } from "../../../hooks/useAppTheme";
import React, { useEffect, useRef, useState } from "react";
import { View, Text } from "../../../components/shared/Themed";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import type { ThemeColors } from "../../../utils/constants/colors";
import {
  Dimensions,
  Image,
  TouchableOpacity,
  StyleSheet,
  ImageSourcePropType,
} from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { ScrollView } from "react-native-gesture-handler";
import { ActivityIndicator } from "react-native-paper";
import { getS3SignUrl } from "../../../services/s3BucketService";
import { validSignedURLWithExt } from "../../../utils/validateSignedUrl";
import { translateFields, useTranslation } from "../../../utils/useTranslation";
import { pmxInventoryTranslationMap } from "../../../utils/translations";

interface OrderItem {
  InvntryUom?: string;
  ItemCode?: string;
  ItemName?: string;
  Location?: string;
  QuantityUom?: string;
  QuantityUom2?: string;
  Uom2?: string;
  Warehouse?: string;
  image?: string;
  PicturName?: string;
}

interface RouteParams {
  order: OrderItem;
}

interface DetailItem {
  label: string;
  value: string;
  supportingValue?: string;
}

type SpecialLabels = {
  [key: string]: {
    oldLabel: string;
    newLabel: string;
    newValue?: string;
    supportingValue?: string;
  };
};

const PmxInventoryDetailsReportScreen = () => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const { order } = route.params || { order: {} };
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width,
  );
  const navigation = useNavigation();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(false);
  const { t } = useTranslation();

  // Determine if we're on a small screen (mobile)
  const isMobileView = windowWidth < 768;

  useEffect(() => {
    // Redirect if order is invalid
    if (!order || typeof order === "string") {
      (navigation as any).navigate("WarehouseInventory");
      return;
    }

    // Track screen size changes
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    });

    // Cleanup on unmount
    return () => subscription?.remove();
  }, [order, navigation]);

  const DrawerHeader = () => {
    return (
      <View style={styles.drawerHeaderStyle}>
        <View style={styles.headerRow}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => (navigation as any).navigate("WarehouseInventory")}
          >
            <AntDesign name="arrowleft" size={24} color={colors.text} />
          </TouchableOpacity>

          <View style={styles.headerTitleContainer}>
            <Text style={styles.headerTitle}>
              {`${t("itemCode")}: ${order?.ItemCode || ""}`}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const ProductDetails = () => {
    const excludedKeys = ["ItemCode", "InvntryUom", "Uom2"];

    const orderedKeys = [
      "ItemName",
      "Warehouse",
      "Location",
      "QuantityUom",
      "QuantityUom2",
    ];

    const specialLabels: SpecialLabels = {
      QuantityUom: {
        oldLabel: "QuantityUom",
        newLabel: t("piece"),
      },
      QuantityUom2: {
        oldLabel: "QuantityUom2",
        newLabel: t("box"),
      },
    };

    const detailItems: DetailItem[] = orderedKeys
      .filter((key) => !excludedKeys.includes(key))
      .map((key) => {
        if (specialLabels[key]) {
          const special = specialLabels[key];
          return {
            label: special.newLabel,
            value: special.newValue || order?.[key]?.toLocaleString() || "-",
            supportingValue: special.supportingValue,
          };
        } else {
          return {
            label: translateFields(key, t, pmxInventoryTranslationMap) || key,
            value: order?.[key as keyof OrderItem]?.toLocaleString() || "-",
          };
        }
      });
    return (
      <View style={styles.productDetailsContainer}>
        <View style={styles.header}>
          <Text style={styles.title}>{order?.ItemCode || ""}</Text>
          <Text style={styles.subtitle}>{order?.FrgnName || ""}</Text>
        </View>
        <View style={styles.body}>
          {detailItems.map((item, index) => (
            <View key={index} style={styles.detailRow}>
              <Text style={styles.label}>{item.label}:</Text>
              <Text style={styles.value}>{item.value}</Text>
              {item.supportingValue && (
                <Text style={styles.supporting}>({item.supportingValue})</Text>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const prevItemCodeRef = useRef<any>();

  useEffect(() => {
    if (order?.PicturName) {
      if (order.PicturName !== prevItemCodeRef.current) {
        prevItemCodeRef.current = order.PicturName;
        validSignedURLWithExt(
          order.PicturName || "",
          "label",
          setIsImageLoading,
          setSelectedImage,
        );
      }
    } else {
      getS3SignUrl("multisteps-label", "no-image.png", "getObject", "image/*").then(
        (data) => {
          setSelectedImage(data?.signedUrl as string);
          setIsImageLoading(false);
        },
      );
    }
  }, [order.PicturName]);

  const ProductImage = () => {
    return (
      <View style={styles.imageContainer}>
        {isImageLoading && (
          <ActivityIndicator size="large" style={styles.loader} />
        )}
        <Image
          source={{ uri: selectedImage } as ImageSourcePropType}
          style={styles.productImage}
          resizeMode="contain"
        />
      </View>
    );
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <DrawerHeader />

      <View
        style={[
          styles.contentContainer,
          isMobileView && styles.contentContainerMobile,
        ]}
      >
        {/* Left Side - Order Details */}
        <View
          style={[
            styles.orderDetails,
            isMobileView ? styles.orderDetailsMobile : null,
          ]}
        >
          <View style={styles.detailBox}>
            <ProductDetails />
          </View>
        </View>

        {/* Middle vertical line - only shown in desktop view */}
        {!isMobileView && <View style={styles.verticalLine} />}

        {/* Right Side - Product Image */}
        <View
          style={[
            styles.imageSection,
            isMobileView ? styles.imageSectionMobile : null,
          ]}
        >
          <View style={styles.detailBox}>
            <ProductImage />
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      flexGrow: 1,
      backgroundColor: colors.surface,
    },
    contentContainer: {
      padding: 16,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    contentContainerMobile: {
      flexDirection: "column",
    },
    orderDetails: {
      flex: 1,
      maxWidth: "48%",
    },
    orderDetailsMobile: {
      maxWidth: "100%",
      marginBottom: 16,
    },
    imageSection: {
      flex: 1,
      maxWidth: "48%",
    },
    imageSectionMobile: {
      maxWidth: "100%",
      marginTop: 20,
    },
    detailBox: {
      borderRadius: 14,
      padding: 16,
      height: "100%",
    },
    detailRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    verticalLine: {
      width: 1,
      backgroundColor: colors.border,
      marginHorizontal: 16,
    },
    drawerHeaderStyle: {
      width: "100%",
      zIndex: 999,
      backgroundColor: colors.surface,
      height: 56,
      borderBottomWidth: 1,
      borderColor: colors.border,
      justifyContent: "center",
      paddingHorizontal: 16,
    },
    headerRow: {
      flexDirection: "row",
      alignItems: "center",
    },
    backButton: {
      paddingVertical: 8,
      paddingRight: 16,
    },
    headerTitleContainer: {
      flex: 1,
    },
    headerTitle: {
      fontWeight: "700",
      fontSize: 16,
    },
    // Product Details Styles
    productDetailsContainer: {
      flex: 1,
    },
    header: {
      marginBottom: 16,
    },
    title: {
      fontSize: 20,
      fontWeight: "bold",
      marginBottom: 4,
    },
    subtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 16,
    },
    body: {
      flex: 1,
    },
    label: {
      fontWeight: "600",
      fontSize: 14,
      flex: 1,
    },
    value: {
      fontSize: 14,
      color: colors.text,
      flex: 1,
      textAlign: "right",
    },
    supporting: {
      fontSize: 12,
      color: colors.textMuted,
      marginLeft: 8,
    },
    // Image Styles
    imageContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      minHeight: 250,
    },
    productImage: {
      width: "100%",
      height: "100%",
      flex: 1,
      minHeight: 250,
    },
    loader: {
      position: "absolute",
      zIndex: 1,
    },
  });
}

export default PmxInventoryDetailsReportScreen;
